import { useContext, createContext } from "react";
import { flushSync } from "react-dom";
import { useLocalStorage } from "react-use";
import { type FormValues } from "@/auth/schema";
import { TOKEN_KEY } from "@/contants";
import { createApiClient } from "@/lib/api-client";
import { apolloClient } from "@/lib/apollo-client";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";

const client = createApiClient({
  baseUrl: import.meta.env.VITE_API_DOMAIN || "",
  options: {
    headers: {
      "Content-Type": "application/json",
    },
    throwHttpErrors: false,
  },
});

export type AuthState =
  | {
      login: (credentials: FormValues) => Promise<{ success: true; token: string } | { success: false; error: string }>;
      logout: () => Promise<void>;
      token: string | undefined;
      isAuthenticated: boolean;
    }
  | undefined;

const AuthContext = createContext<AuthState>({
  token: undefined,
  login: () => Promise.resolve({ success: false, error: "" }),
  logout: () => Promise.resolve(),
  isAuthenticated: false,
});

type LoginResponse = { token: string } | { error: unknown } | { errors: Array<unknown> };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken, removeToken] = useLocalStorage<string>(TOKEN_KEY, "", { raw: true });
  const isAuthenticated = Boolean(token);

  const login = async (credentials: FormValues) => {
    const response = await client.post<LoginResponse>("sign-in/", {
      json: credentials,
    });

    const json = await response.json();
    if ("token" in json) {
      // https://github.com/TanStack/router/issues/2072
      // https://github.com/TanStack/router/discussions/1668
      flushSync(() => {
        setToken(json.token);
      });

      return { success: true, token: json.token } as const;
    }

    if ("errors" in json) {
      const errorMessages = extractErrorMessages(json.errors);
      return { success: false, error: errorMessages } as const;
    }

    return { success: false, error: getErrorMessage(json.error) } as const;
  };

  const logout = async () => {
    removeToken();
    localStorage.clear();
    await apolloClient.clearStore();
  };

  const value = { login, token, isAuthenticated, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
